import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/UI/Button";
import TextInput from "../../../../../components/UI/TextInput";
import Modal from "../../../../../components/UI/Modal";
import Selection from "../../../../../components/UI/Selection";
import { useAuthStore } from "../../../../../stores/auth-store";
import { companyShopUseCase } from "../../../../../../di/container";
import type { ShopBannerEntity } from "../../../../../../domain/types/company-shop";
import { onError } from "../../utils";

interface EditBannerFormProps {
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isModalVisible: boolean;
  bannersData: ShopBannerEntity[] | null;
  setBannersData: React.Dispatch<
    React.SetStateAction<ShopBannerEntity[] | null>
  >;
  reloadBannersData: () => void;
  editingBannerId: number | null;
}

export const EditBannerForm = ({
  setIsModalVisible,
  isModalVisible,
  reloadBannersData,
  bannersData,
  setBannersData,
  editingBannerId,
}: EditBannerFormProps) => {
  const { user } = useAuthStore();

  interface EditBannerForm {
    bannerImage: FileList;
    actionType: "PRODUCT" | "NOTICE" | "FAQ" | "CUSTOM" | "NONE";
    actionValue: string;
    name: string;
  }
  const editBannerForm = useForm<EditBannerForm>({
    defaultValues: {
      actionType: "NONE",
    },
  });

  const bannerFormBannerWatch = editBannerForm.watch("bannerImage");

  const getCurrentBanner = () => {
    if (bannersData && editingBannerId) {
      for (let i = 0; i < bannersData?.length; i++) {
        if (editingBannerId === bannersData[i].id) {
          return bannersData[i];
        }
      }
      return null;
    } else {
      return null;
    }
  };

  const [selectedOption, setSelectedOption] = useState<string>("NONE");
  useEffect(() => {
    switch (selectedOption) {
      case "NONE":
        editBannerForm.setValue("actionType", "NONE");
        break;
      case "PRODUCT":
        editBannerForm.setValue("actionType", "PRODUCT");
        break;
      case "NOTICE":
        editBannerForm.setValue("actionType", "NOTICE");
        break;
      case "FAQ":
        editBannerForm.setValue("actionType", "FAQ");
        break;
      case "CUSTOM":
        editBannerForm.setValue("actionType", "CUSTOM");
        break;
      default:
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  // 폼 초기값 불러오기
  const [bannerImageUrl, setBannerImageUrl] = useState<string>("");
  useEffect(() => {
    const currentBanner = getCurrentBanner();
    if (currentBanner) {
      setBannerImageUrl(currentBanner.bannerUrl ?? "");
      editBannerForm.setValue("name", currentBanner.title ?? "");
      editBannerForm.setValue("actionValue", currentBanner.actionType ?? "");
      editBannerForm.setValue("actionValue", currentBanner.actionValue ?? "");

      switch (currentBanner.actionType) {
        case "PRODUCT":
          setSelectedOption("PRODUCT");
          break;
        case "NOTICE":
          setSelectedOption("NOTICE");
          break;
        case "FAQ":
          setSelectedOption("FAQ");
          break;
        case "CUSTOM":
          setSelectedOption("CUSTOM");
          break;
        case "NONE":
          setSelectedOption("NONE");
          break;
        default:
          setSelectedOption("NONE");
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBannerId, bannersData]);

  const newBannerFormBannerPreviewUrl =
    bannerFormBannerWatch && bannerFormBannerWatch.length > 0
      ? URL.createObjectURL(bannerFormBannerWatch[0])
      : bannerImageUrl;

  const onNewBannerFormSubmit = async (data: EditBannerForm) => {
    const file =
      data.bannerImage && data.bannerImage.length > 0
        ? data.bannerImage[0]
        : undefined;

    if (user?.companyId && editingBannerId) {
      await companyShopUseCase.updateBanner({
        companyId: user?.companyId,
        bannerId: editingBannerId,
        bannerFile: file,
        command: {
          title: data.name,
          actionType: data.actionType,
          actionValue: data.actionType === "NONE" ? null : data.actionValue,
        },
      });
    }

    reloadBannersData();
    editBannerForm.reset();
    setIsModalVisible(false);
  };

  return (
    <>
      {isModalVisible && (
        <Modal setIsModalVisible={setIsModalVisible} title="">
          <form
            onSubmit={editBannerForm.handleSubmit(
              onNewBannerFormSubmit,
              onError,
            )}
          >
            <div className="w-[calc(100vw-20vw)] max-w-[31.875rem]">
              <div className="flex flex-col items-start mb-[1.0625rem]">
                <h2 className="mb-[0.75rem]">배너 수정</h2>
              </div>

              <label>
                <div
                  className="relative w-full h-[150px] mb-[1.0625rem] outline-dashed outline-gray-200
            flex justify-center items-center text-gray-300"
                >
                  <div className="absolute">이미지를 업로드해주세요.</div>
                  {newBannerFormBannerPreviewUrl && (
                    <img
                      className="relative z-30 h-full"
                      src={newBannerFormBannerPreviewUrl}
                      alt="프리뷰 이미지"
                    />
                  )}
                </div>

                <input
                  type="file"
                  className="text-start hidden"
                  accept=".png,.jpg,.jpeg,.pdf"
                  {...editBannerForm.register("bannerImage", {
                    required: {
                      value: !Boolean(bannerImageUrl),
                      message: "필수 항목이에요.",
                    },
                  })}
                />
              </label>

              <div className="text-start mb-[1.0625rem]">
                <label className="block text-sm font-medium mb-2">이름</label>
                <TextInput
                  {...editBannerForm.register("name", {
                    required: {
                      value: true,
                      message: "필수 항목이에요.",
                    },
                  })}
                  error={{
                    message:
                      editBannerForm.formState.errors.name?.message || "",
                    isIconVisible: true,
                    isMessageVisible: true,
                  }}
                  placeholder="배너 이름 입력"
                  className="bg-gray-50"
                />
              </div>

              <div className="h-[45px] text-start mb-[40px]">
                <label className="block text-sm font-medium mb-[6px]">
                  액션 타입
                </label>
                <Selection
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                  options={[
                    { value: "NONE", name: "없음" },
                    { value: "PRODUCT", name: "상품" },
                    { value: "NOTICE", name: "공지사항" },
                    { value: "FAQ", name: "자주 묻는 질문" },
                    { value: "CUSTOM", name: "커스텀" },
                  ]}
                ></Selection>
              </div>

              {!(selectedOption === "NONE") ? (
                <div className="text-start mb-[1.0625rem]">
                  <label className="block text-sm font-medium mb-2">
                    액션 URL
                  </label>
                  <TextInput
                    {...editBannerForm.register("actionValue", {})}
                    error={{
                      message:
                        editBannerForm.formState.errors.actionValue?.message ||
                        "",
                      isIconVisible: true,
                      isMessageVisible: true,
                    }}
                    placeholder="배너 액션 URL"
                    className="bg-gray-50"
                  />
                </div>
              ) : null}

              <div className="relative">
                <div className="absolute top-0 bottom-0 left-0 right-0 mx-[-19px] bg-[#F9FAFB] outline outline-1 outline-[#E5E7EB] rounded-b-[12px]"></div>
                <div className="relative z-50 flex flex-row justify-end py-[1rem] mb-[-20px] items-center">
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="danger"
                      className="w-[4.014375rem] h-[2.5625rem]"
                      onClick={async (e) => {
                        e.preventDefault();

                        // prompt
                        const promptResult =
                          window.confirm("정말로 삭제하시겠습니까?");

                        if (
                          user?.companyId &&
                          editingBannerId &&
                          promptResult
                        ) {
                          await companyShopUseCase.deleteBanner({
                            companyId: user?.companyId,
                            bannerId: editingBannerId,
                          });

                          reloadBannersData();
                          editBannerForm.reset();
                          setIsModalVisible(false);
                        }
                      }}
                    >
                      삭제
                    </Button>
                    <Button className="w-[4.014375rem] h-[2.5625rem]">
                      완료
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};
